import { apiClient } from '../../axiosInstance';
import { ENDPOINTS } from '../../endpoints';

export const VIDEO_CHUNK_BYTES = 10 * 1024 * 1024; // 10 MB per D-6
export const VIDEO_MAX_BYTES = 500 * 1024 * 1024; // 500 MB per D-8
export const ALLOWED_VIDEO_EXT = [
  'mp4',
  'mov',
  'avi',
  'webm',
  'm4v',
  'mkv',
] as const;

export type VideoProgressCb = (e: { file: File; percent: number }) => void;
export type VideoStartCb = (file: File) => void;

export function putChunkXHR(
  url: string,
  body: Blob,
  onProgress?: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded);
    xhr.onload = () => {
      const etag = (xhr.getResponseHeader('etag') ?? '').replace(/"/g, '');
      if (!etag) {
        reject(new Error('No ETag received from S3'));
        return;
      }
      resolve(etag);
    };
    xhr.onerror = (e) => reject(e);
    xhr.onabort = () =>
      reject(new DOMException('Upload aborted', 'AbortError'));
    if (signal) {
      if (signal.aborted) {
        // Aborting in the OPENED (pre-send) state does not dispatch `onabort`
        // per the XHR spec, so the handler above would never settle this
        // Promise. Reject directly instead of relying on the event.
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}

export async function uploadChunks(
  file: File,
  parts: Array<{ part_number: number; url: string }>,
  onProgress?: VideoProgressCb,
  signal?: AbortSignal,
) {
  const completedParts: { part_number: number; etag: string }[] = [];
  let cumulativeLoaded = 0;
  for (const { part_number, url } of parts) {
    if (signal?.aborted) {
      throw new DOMException('Upload aborted', 'AbortError');
    }
    const start = (part_number - 1) * VIDEO_CHUNK_BYTES;
    const end = Math.min(start + VIDEO_CHUNK_BYTES, file.size);
    const chunk = file.slice(start, end);
    const etag = await putChunkXHR(
      url,
      chunk,
      (loaded) => {
        const pct = Math.round(((cumulativeLoaded + loaded) / file.size) * 100);
        onProgress?.({ file, percent: Math.min(pct, 99) });
      },
      signal,
    );
    cumulativeLoaded = end;
    completedParts.push({ part_number, etag });
  }
  return completedParts;
}

// eslint-disable-next-line max-lines-per-function -- initiate → reserve part URLs → upload chunks → complete → cleanup-on-error, with optional abort signal threaded through each step; splitting fragments the multipart flow
export async function uploadSingleVideo(
  file: File,
  jobId: number,
  onProgress?: VideoProgressCb,
  onStart?: VideoStartCb,
  signal?: AbortSignal,
) {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  if (!(ALLOWED_VIDEO_EXT as readonly string[]).includes(ext)) {
    throw new Error(`Disallowed extension: ${ext}`);
  }
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error('Video exceeds 500 MB cap');
  }

  let key: string | undefined;
  let upload_id: string | undefined;
  try {
    onStart?.(file);
    const initResp = await apiClient.post(
      ENDPOINTS.v2.jobs.videosInitiateMultipart(jobId),
      { extension: ext, content_type: file.type || `video/${ext}` },
      { signal },
    );
    key = initResp.data.key;
    upload_id = initResp.data.upload_id;

    const partCount = Math.ceil(file.size / VIDEO_CHUNK_BYTES);
    const partNumbers = Array.from({ length: partCount }, (_, i) => i + 1);
    const { data: partData } = await apiClient.post(
      ENDPOINTS.v2.videos.partUrls,
      { key, upload_id, part_numbers: partNumbers },
      { signal },
    );

    const completedParts = await uploadChunks(
      file,
      partData.parts,
      onProgress,
      signal,
    );

    await apiClient.post(
      ENDPOINTS.v2.jobs.videosCompleteMultipart(jobId),
      {
        key,
        upload_id,
        parts: completedParts,
        source: 'customer_portal',
        filename: file.name,
      },
      { signal },
    );
    onProgress?.({ file, percent: 100 });
  } catch (err) {
    if (key && upload_id) {
      // Abort the multipart upload on S3 regardless of why we got here.
      // Don't pass the (possibly aborted) signal — we want this cleanup
      // call to actually fire.
      await apiClient
        .post(ENDPOINTS.v2.videos.abortUpload, { key, upload_id })
        .catch((_e: unknown) => undefined);
    }
    throw err;
  }
}
